import { useState, useRef } from "react";
import { api, API_BASE } from "../lib/api";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertCircle, Download } from "lucide-react";
import { toast } from "sonner";

export default function AdminBulkUpload() {
    const [file, setFile] = useState(null);
    const [result, setResult] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [sendWelcome, setSendWelcome] = useState(true);
    const fileInputRef = useRef(null);

    const onSelect = (e) => {
        const f = e.target.files?.[0];
        if (f) setFile(f);
    };

    const submit = async () => {
        if (!file) {
            toast.error("Please choose a CSV or Excel file");
            return;
        }
        setUploading(true);
        setResult(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("send_welcome", sendWelcome ? "true" : "false");
            const r = await api.post("/admin/parents/bulk-upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(r.data);
            toast.success(`Imported ${r.data.created} parents${sendWelcome ? ` · ${r.data.welcome_emails_queued} welcome emails queued` : ""}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const downloadSample = async () => {
        try {
            const token = localStorage.getItem("ccc_token");
            const resp = await fetch(`${API_BASE}/admin/sample-csv`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!resp.ok) throw new Error("Failed");
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "childhood_parents_sample.csv";
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error("Could not download sample CSV");
        }
    };

    return (
        <div className="space-y-6 max-w-3xl" data-testid="admin-bulk-upload-page">
            <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Tools</div>
                <h1 className="font-heading text-3xl font-black tracking-tight mt-1">Bulk Upload Parents</h1>
                <p className="text-muted-foreground text-sm mt-1">Import multiple parents from a CSV or Excel file.</p>
            </div>

            <Card className="rounded-2xl">
                <CardHeader>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                            <CardTitle className="font-heading">Required Columns</CardTitle>
                            <CardDescription>Your file must include these column headers (case-insensitive, spaces allowed):</CardDescription>
                        </div>
                        <Button onClick={downloadSample} variant="outline" className="rounded-full" data-testid="download-sample-csv-btn">
                            <Download className="h-4 w-4 mr-2" /> Sample CSV
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                        <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600" /> <strong>Parent Name</strong> (required)</li>
                        <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600" /> <strong>Email</strong> (required)</li>
                        <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-muted-foreground" /> Mobile Number</li>
                        <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-muted-foreground" /> Address</li>
                        <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-muted-foreground" /> Child Name (comma-separated)</li>
                    </ul>
                </CardContent>
            </Card>

            <Card className="rounded-2xl">
                <CardContent className="p-6">
                    <div className="border-2 border-dashed border-border rounded-2xl p-8 text-center">
                        <FileSpreadsheet className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                        <p className="text-sm text-muted-foreground mb-3">
                            {file ? <><span className="font-bold text-foreground">{file.name}</span> ({(file.size / 1024).toFixed(1)} KB)</> : "Choose a .csv or .xlsx file to import"}
                        </p>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,.xlsx,.xls"
                            onChange={onSelect}
                            className="hidden"
                            data-testid="bulk-file-input"
                        />
                        <div className="flex flex-wrap gap-2 justify-center">
                            <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="rounded-full" data-testid="bulk-choose-file-btn">
                                Choose file
                            </Button>
                            <Button onClick={submit} disabled={!file || uploading} className="rounded-full" data-testid="bulk-upload-btn">
                                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Upload className="h-4 w-4 mr-2" /> Upload</>)}
                            </Button>
                        </div>
                    </div>
                    <div className="mt-4 flex items-center justify-between bg-muted rounded-2xl px-4 py-3">
                        <div>
                            <Label className="font-semibold text-sm">Send welcome email to imported parents</Label>
                            <div className="text-xs text-muted-foreground">Introduces the portal and how to sign in with OTP.</div>
                        </div>
                        <Switch checked={sendWelcome} onCheckedChange={setSendWelcome} data-testid="bulk-welcome-toggle" />
                    </div>
                </CardContent>
            </Card>

            {result && (
                <Card className="rounded-2xl" data-testid="bulk-result-card">
                    <CardHeader>
                        <CardTitle className="font-heading flex items-center gap-2">
                            <CheckCircle2 className="h-5 w-5 text-green-600" /> Import Complete
                        </CardTitle>
                        <CardDescription>
                            Created <strong>{result.created}</strong> of <strong>{result.total_rows}</strong> rows.
                        </CardDescription>
                    </CardHeader>
                    {result.errors?.length > 0 && (
                        <CardContent>
                            <div className="font-semibold mb-2 text-sm flex items-center gap-2">
                                <AlertCircle className="h-4 w-4 text-amber-600" /> {result.errors.length} issue(s)
                            </div>
                            <div className="max-h-60 overflow-auto border border-border rounded-xl divide-y divide-border">
                                {result.errors.map((e, i) => (
                                    <div key={i} className="px-4 py-2 text-sm flex justify-between gap-3">
                                        <span className="text-muted-foreground">Row {e.row}</span>
                                        <span>{e.error}</span>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    )}
                </Card>
            )}
        </div>
    );
}
